import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import translations from '../utils/translations';

/**
 * AppContext — centralized state for:
 *  - Language (en | fr | ar)
 *  - Theme (light | dark)
 *  - Cart (items, add, remove, clear, total)
 *  - UI translations (t function)
 */

const AppContext = createContext(null);

export function AppProvider({ children }) {
  // ── Language ──────────────────────────────────────────────
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('qrmenu-lang') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('qrmenu-lang', language);
    // Set text direction for Arabic
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  // Translation helper: t('menu') → "القائمة" if lang=ar
  const t = useCallback(
    (key) => translations[language]?.[key] || translations.en[key] || key,
    [language]
  );

  // Localized text helper for menu data: loc({ en: "Pizza", ar: "بيتزا" })
  const loc = useCallback(
    (obj) => {
      if (!obj) return '';
      if (typeof obj === 'string') return obj;
      return obj[language] || obj.en || Object.values(obj)[0] || '';
    },
    [language]
  );

  // ── Theme ────────────────────────────────────────────────
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('qrmenu-theme');
    if (saved) return saved;
    // Respect system preference
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
    return 'light';
  });

  useEffect(() => {
    localStorage.setItem('qrmenu-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  // ── Cart ─────────────────────────────────────────────────
  const [cart, setCart] = useState([]);
  const [cartNotification, setCartNotification] = useState(null);

  const addToCart = useCallback((item) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) {
        return prev.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    // Show notification
    setCartNotification(item.id);
    setTimeout(() => setCartNotification(null), 1500);
  }, []);

  const removeFromCart = useCallback((itemId) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === itemId);
      if (existing && existing.quantity > 1) {
        return prev.map((i) =>
          i.id === itemId ? { ...i, quantity: i.quantity - 1 } : i
        );
      }
      return prev.filter((i) => i.id !== itemId);
    });
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // ── Context value ────────────────────────────────────────
  const value = {
    language,
    setLanguage,
    t,
    loc,
    theme,
    toggleTheme,
    cart,
    addToCart,
    removeFromCart,
    clearCart,
    cartTotal,
    cartCount,
    cartNotification,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/** Hook to access the app context */
export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
